import { fetchAuthTokens } from "../actions/auth";

interface FetchOptions extends RequestInit {
  responseType?: "json" | "text" | "blob" | "formData" | "arrayBuffer";
  timeout?: number; // Timeout in milliseconds
  retries?: number; // Number of retry attempts
  isAuth?: boolean;
}

const fetchWrapper = async <T>(
  url: string,
  options: FetchOptions = {},
  attempt: number = 0
): Promise<T> => {
  const {
    responseType = "json",
    timeout = 5000,
    retries = 0,
    headers,
    isAuth = false,
    ...fetchOptions
  } = options;

  const timeoutPromise = new Promise<never>((_, reject) =>
    setTimeout(() => reject(new Error("Request timed out")), timeout)
  );
  const fetchPromise = fetch(url, { ...fetchOptions, headers }).then(
    async (response) => {
      if (response.status === 403 && isAuth && attempt < 2) {
        const newToken = await fetchAuthTokens();
        const newHeaders = {
          ...headers,
          Authorization: `Bearer ${newToken.AccessToken}`,
        };
        return fetchWrapper<T>(
          url,
          { ...options, headers: newHeaders },
          attempt + 1
        );
      }
      if (!response.ok) {
        const responseText = await response.text();
        const responseData = JSON.parse(responseText);
        const errorMessage =
          responseData.error ||
          responseData.message ||
          "Unknown error occurred";
        throw new Error(errorMessage);
      }

      switch (responseType) {
        case "text":
          return response.text() as unknown as T;
        case "blob":
          return response.blob() as unknown as T;
        case "formData":
          return response.formData() as unknown as T;
        case "arrayBuffer":
          return response.arrayBuffer() as unknown as T;
        case "json":
        default:
          return response.json() as unknown as T;
      }
    }
  );

  try {
    return await Promise.race([fetchPromise, timeoutPromise]);
  } catch (error) {
    if (attempt < retries) {
      return fetchWrapper(url, options, attempt + 1);
    } else {
      throw error;
    }
  }
};

export default fetchWrapper;
